import { ForwardedRef, forwardRef, useMemo } from "react"
import ReactSelect, {
  ClassNamesConfig,
  ClearIndicatorProps,
  DropdownIndicatorProps,
  GroupBase,
  GroupHeadingProps,
  InputProps,
  MultiValueGenericProps,
  MultiValueRemoveProps,
  NoticeProps,
  OptionProps,
  PlaceholderProps,
  SelectInstance,
  SingleValueProps,
  components,
} from "react-select"
import ReactSelectAsync, { AsyncProps } from "react-select/async"
import type { Props } from "react-select/base"
import ReactSelectCreatable, { CreatableProps } from "react-select/creatable"

import { cn } from "@/lib/utils"
import { IconChevronDown, IconX } from "@tabler/icons-react"

import { Tag } from "./Tag"

// https://react-select.com/typescript#custom-select-props
declare module "react-select/base" {
  export interface Props<
    Option,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    IsMulti extends boolean,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    Group extends GroupBase<Option>,
  > {
    isError?: boolean
  }
}

export type SelectProps<
  Option = unknown,
  IsMulti extends boolean = boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
> = Props<Option, IsMulti, Group>

export type CreatableSelectProps<
  Option = unknown,
  IsMulti extends boolean = boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
> = CreatableProps<Option, IsMulti, Group>

export type AsyncSelectProps<
  Option = unknown,
  IsMulti extends boolean = boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
> = AsyncProps<Option, IsMulti, Group>

/**
 * Select uses react-select as a base, see their docs for usage: https://react-select.com/
 */
export const Select = forwardRef(function Select<
  Option,
  IsMulti extends boolean = boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: SelectProps<Option, IsMulti, Group>,
  ref: ForwardedRef<SelectInstance<Option, IsMulti, Group>>,
) {
  const classNames: typeof props.classNames = useMemo(() => {
    return getClassNames({ isError: props.isError, classNames: props?.classNames })
  }, [props?.classNames, props.isError])

  return (
    <ReactSelect
      {...props}
      menuPlacement={props.menuPlacement ?? "auto"}
      ref={ref}
      unstyled
      classNames={classNames}
      styles={{
        menu: (base, state) => ({
          ...base,
          zIndex: undefined,
          pointerEvents: "auto",
          ...props.styles?.menu?.(base, state),
        }),
        menuPortal: (base) => ({
          ...base,
          zIndex: undefined,
        }),
        control: (base, state) => ({
          ...base,
          minHeight: undefined,
          ...props.styles?.control?.(base, state),
        }),
        ...props.styles,
      }}
      components={selectComponents}
    />
  )
}) as ReactSelect

/**
 * Select uses react-select as a base, see their docs for usage: https://react-select.com/
 */
export const AsyncSelect = forwardRef(function AsyncSelect<
  Option,
  IsMulti extends boolean = boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: AsyncSelectProps<Option, IsMulti, Group>,
  ref: ForwardedRef<SelectInstance<Option, IsMulti, Group>>,
) {
  const classNames: typeof props.classNames = useMemo(() => {
    return getClassNames({ isError: props.isError, classNames: props?.classNames })
  }, [props?.classNames, props.isError])

  return (
    <ReactSelectAsync
      menuPlacement="auto"
      {...props}
      ref={ref}
      unstyled
      classNames={classNames}
      styles={{
        menu: (base, state) => ({
          ...base,
          zIndex: undefined,
          ...props.styles?.menu?.(base, state),
        }),
        menuPortal: (base) => ({
          ...base,
          zIndex: undefined,
        }),
        control: (base, state) => ({
          ...base,
          minHeight: undefined,
          ...props.styles?.control?.(base, state),
        }),
        ...props.styles,
      }}
      components={selectComponents}
    />
  )
}) as ReactSelectAsync

/**
 * Select uses react-select as a base, see their docs for usage: https://react-select.com/
 */
export const CreatableSelect = forwardRef(function CreatableSelect<
  Option,
  IsMulti extends boolean = boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: CreatableSelectProps<Option, IsMulti, Group>,
  ref: ForwardedRef<SelectInstance<Option, IsMulti, Group>>,
) {
  const classNames: typeof props.classNames = useMemo(() => {
    return getClassNames({ isError: props.isError, classNames: props?.classNames })
  }, [props?.classNames, props.isError])

  return (
    <ReactSelectCreatable
      {...props}
      ref={ref}
      unstyled
      classNames={classNames}
      styles={{
        menu: (base) => ({
          ...base,
          zIndex: undefined,
        }),
        menuPortal: (base) => ({
          ...base,
          zIndex: undefined,
        }),
        control: (base) => ({
          ...base,
          minHeight: undefined,
        }),
      }}
      components={selectComponents}
      menuPlacement="auto"
    />
  )
}) as ReactSelectCreatable

function getClassNames<
  Option = unknown,
  IsMulti extends boolean = boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  isError,
  classNames,
}: {
  isError?: boolean
  classNames?: ClassNamesConfig<Option, IsMulti, Group>
}): ClassNamesConfig<Option, IsMulti, Group> {
  return {
    ...classNames,
    control: (props) =>
      cn(
        /*tw*/ "group min-h-[40px] gap-2 bg-white px-3 py-2 text-colors-text-primary !transition-all",
        /*tw*/ "rounded border border-grey-400",
        /*tw*/ "hover:border-colors-border-hover hover:bg-colors-bg-hover",
        /*tw*/ "focus-within:border-colors-border-focus",
        "",
        {
          /*tw*/ "[&_.le-indicator-container]:rotate-180": props.menuIsOpen,
          /*tw*/ "border-colors-border-error": isError,
          /*tw*/ "border-colors-border-disabled bg-colors-bg-disabled text-colors-text-placeholder":
            props.isDisabled,
        },
        classNames?.control?.(props),
      ),
    placeholder: (props) =>
      cn(
        /*tw*/ "text-colors-text-placeholder",
        {
          /*tw*/ "text-colors-text-placeholder": props.isDisabled,
        },
        classNames?.placeholder?.(props),
      ),
    dropdownIndicator: (props) =>
      cn(
        /*tw*/ "le-indicator-container text-colors-icon-secondary !transition-all",
        {
          /*tw*/ "text-colors-text-placeholder": props.isDisabled,
        },
        classNames?.dropdownIndicator?.(props),
      ),
    menu: (props) =>
      cn(/*tw*/ "z-50 my-1 text-colors-text-primary", classNames?.menu?.(props)),
    menuPortal: (props) => cn(/*tw*/ "z-[200]", classNames?.menuPortal?.(props)),
    menuList: (props) =>
      cn(
        /*tw*/ "rounded border border-colors-border-secondary bg-white p-3 shadow",
        classNames?.menuList?.(props),
      ),
    option: (props) =>
      cn(
        /*tw*/ "rounded p-3 break-words transition-all hover:bg-colors-bg-hover",
        /*tw*/ "aria-disabled:text-colors-text-disabled aria-disabled:hover:bg-transparent",
        {
          /*tw*/ "bg-colors-bg-hover": props.isFocused,
        },
        classNames?.option?.(props),
      ),
    multiValue: (props) => cn(/*tw*/ "gap-1", classNames?.multiValue?.(props)),
    valueContainer: (props) =>
      cn(/*tw*/ "-my-1 -mr-1 gap-1", classNames?.valueContainer?.(props)),
    clearIndicator: (props) =>
      cn(/*tw*/ "mr-2 text-colors-icon-secondary", classNames?.clearIndicator?.(props)),
    groupHeading: (props) =>
      cn(/*tw*/ "py-2 text-colors-text-secondary", classNames?.groupHeading?.(props)),
  }
}

const Placeholder = <
  Option = unknown,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: PlaceholderProps<Option, IsMulti, Group>,
) => {
  return (
    <components.Placeholder {...props}>
      <span className="text-body1">{props.children}</span>
    </components.Placeholder>
  )
}

const SingleValue = <
  Option = unknown,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: SingleValueProps<Option, IsMulti, Group>,
) => {
  return (
    <components.SingleValue {...props}>
      <span className="text-body1">{props.children}</span>
    </components.SingleValue>
  )
}

const DropdownIndicator = <
  Option = unknown,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: DropdownIndicatorProps<Option, IsMulti, Group>,
) => {
  return (
    <components.DropdownIndicator {...props}>
      <IconChevronDown />
    </components.DropdownIndicator>
  )
}

const ClearIndicator = <
  Option = unknown,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: ClearIndicatorProps<Option, IsMulti, Group>,
) => {
  return (
    <components.ClearIndicator {...props}>
      <IconX />
    </components.ClearIndicator>
  )
}

const Option = <
  Option = unknown,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: OptionProps<Option, IsMulti, Group>,
) => {
  return (
    <components.Option {...props}>
      <span className="text-body1">{props.children}</span>
    </components.Option>
  )
}

const MultiValueContainer = <
  Option = unknown,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: MultiValueGenericProps<Option, IsMulti, Group>,
) => {
  return (
    <Tag
      variant="neutral"
      activeOnGroupHover
      label={<components.MultiValueContainer {...props} />}
      className="min-w-0"
    />
  )
}

const MultiValueRemove = <
  Option = unknown,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: MultiValueRemoveProps<Option, IsMulti, Group>,
) => {
  return (
    <components.MultiValueRemove {...props}>
      <IconX className="cursor-pointer" />
    </components.MultiValueRemove>
  )
}

const NoOptionsMessage = <
  Option = unknown,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: NoticeProps<Option, IsMulti, Group>,
) => {
  return (
    <components.NoOptionsMessage {...props}>
      <span className="text-body1">{props.children}</span>
    </components.NoOptionsMessage>
  )
}

const Input = <
  Option = unknown,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: InputProps<Option, IsMulti, Group>,
) => {
  return <components.Input<Option, IsMulti, Group> className="text-body1" {...props} />
}

const GroupHeading = <
  Option = unknown,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: GroupHeadingProps<Option, IsMulti, Group>,
) => {
  return (
    <components.GroupHeading {...props}>
      <span className="text-body1">{props.children}</span>
    </components.GroupHeading>
  )
}

const selectComponents = {
  Placeholder,
  DropdownIndicator,
  Option,
  NoOptionsMessage,
  SingleValue,
  MultiValueContainer,
  MultiValueRemove,
  ClearIndicator,
  Input,
  GroupHeading,
}
